import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import {
  WorkflowService,
  WorkflowTestSet,
  WorkflowTestSetItem,
  WorkflowRun,
} from "../../services/WorkflowService";
import moment from "moment";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import OverflowTabs, { TabItem } from "../ui/overflow-tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AppWindowIcon,
  FileTextIcon,
  CalendarIcon,
  WorkflowIcon,
  CopyIcon,
  ChevronLeftIcon,
  EyeIcon,
  HistoryIcon,
  EditIcon,
  GitCompareArrows,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { notification } from "antd";
import WorkflowRunDetailModal, { formatWorkflowDuration } from "../shared/WorkflowRunDetailModal";
import SmartPagination from "../shared/SmartPagination";
import WorkflowTestRunsTab from "./WorkflowTestRunsTab";
import WorkflowTestRunComparison from "../TestRun/WorkflowTestRunComparison";

const PAGE_SIZE = 10;

const WorkflowTestSetDetail: React.FC = () => {
  const { testSetId } = useParams<{ testSetId: string }>();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();

  const [testSet, setTestSet] = useState<WorkflowTestSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "overview");

  const [items, setItems] = useState<WorkflowTestSetItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPage, setItemsPage] = useState(1);
  const [isItemsLoading, setIsItemsLoading] = useState(false);

  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [isCompareMode, setIsCompareMode] = useState(false);
  const [selectedTestRunIds, setSelectedTestRunIds] = useState<number[]>([]);
  const [selectedTestRunsMap, setSelectedTestRunsMap] = useState<Record<number, boolean>>({});
  const [isComparisonModalVisible, setIsComparisonModalVisible] = useState(false);

  const handleCompareClick = () => {
    setIsCompareMode(true);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  const handleCancelCompare = () => {
    setIsCompareMode(false);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  const handleCompareRuns = () => {
    setIsComparisonModalVisible(true);
  };

  const handleCloseComparisonModal = () => {
    setIsComparisonModalVisible(false);
    setIsCompareMode(false);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  const handleTestRunSelect = (runId: number, checked: boolean) => {
    const updatedMap = { ...selectedTestRunsMap, [runId]: checked };
    setSelectedTestRunsMap(updatedMap);
    setSelectedTestRunIds(
      Object.entries(updatedMap).filter(([, v]) => v).map(([k]) => Number(k))
    );
  };

  useEffect(() => {
    if (!testSetId) return;
    setIsLoading(true);
    WorkflowService.getWorkflowTestSet(
      Number(testSetId),
      PAGE_SIZE,
      0,
      setIsLoading,
      apiKey
    ).subscribe(
      (data: WorkflowTestSet) => {
        setTestSet(data);
        setItems(data.items || []);
        setTotalItems(data.item_count || 0);
        setIsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to load workflow test set: ${error}`,
        });
        setIsLoading(false);
      }
    );
  }, [testSetId, apiKey]);

  useEffect(() => {
    if (!testSetId || itemsPage === 1) return;
    fetchItems(itemsPage);
  }, [itemsPage]);


  const fetchItems = (page: number) => {
    if (!testSetId) return;
    setIsItemsLoading(true);
    const offset = (page - 1) * PAGE_SIZE;
    WorkflowService.getWorkflowTestSet(
      Number(testSetId),
      PAGE_SIZE,
      offset,
      setIsItemsLoading,
      apiKey
    ).subscribe(
      (data: WorkflowTestSet) => {
        setItems(data.items || []);
        setTotalItems(data.item_count || 0);
        setIsItemsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsItemsLoading(false);
      }
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notification.success({ message: "Copied to clipboard", duration: 1.5 });
  };

  const handleRunClick = (item: WorkflowTestSetItem) => {
    if (!item.workflow_run) return;
    setSelectedRun(item.workflow_run);
    setDetailOpen(true);
  };

  const tabs: TabItem[] = [
    { key: "overview", label: "Overview", icon: <AppWindowIcon className="w-4 h-4" /> },
    { key: "runs", label: "Workflow Runs", icon: <WorkflowIcon className="w-4 h-4" /> },
    { key: "test-runs", label: "Test Runs", icon: <HistoryIcon className="w-4 h-4" /> },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">

        {/* Header */}
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <button
                onClick={() => navigate("/test/test-sets")}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Test Sets
              </button>
            </nav>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  {testSet?.test_set_name}
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg flex items-center gap-2">
                  {testSet?.application_name || "Unknown Application"}
                  <Badge variant="secondary" className="font-gc-reg text-xs">Workflow-based</Badge>
                </p>
              </div>
              <Button
                onClick={() => navigate(`/test/workflow-test-sets/${testSetId}/edit`)}
                className="gap-2"
              >
                <EditIcon className="w-4 h-4" />
                Edit
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            <div className="border-b border-border">
              <OverflowTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            </div>

            <div className="p-8">
              {activeTab === "overview" && (
                <div className="space-y-8">
                  {/* Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="border-border">
                      <CardContent className="p-6">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground font-gc-reg">Workflow Runs</p>
                          </div>
                          <p className="text-3xl font-gc-demi-bold text-foreground">
                            {testSet?.item_count ?? 0}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-border">
                      <CardContent className="p-6">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground font-gc-reg">Date Created</p>
                          </div>
                          <p className="text-lg font-gc-med text-foreground">
                            {testSet?.date_created
                              ? moment(testSet.date_created).format("MMM D, YYYY")
                              : "-"}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-border">
                      <CardContent className="p-6">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <AppWindowIcon className="w-4 h-4 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground font-gc-reg">Application</p>
                          </div>
                          <p className="text-lg font-gc-med text-foreground">
                            {testSet?.application_name || "-"}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Details */}
                  <Card className="border-border">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-gc-med text-foreground mb-4">Details</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-muted-foreground font-gc-reg mb-1">Description</p>
                          <p className="text-base font-gc-reg text-foreground whitespace-pre-wrap">
                            {testSet?.test_set_description || "No description provided"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === "test-runs" && testSetId && (
                <WorkflowTestRunsTab
                  testSetId={Number(testSetId)}
                  isCompareMode={isCompareMode}
                  selectedTestRunIds={selectedTestRunIds}
                  selectedTestRunsMap={selectedTestRunsMap}
                  onCompareClick={handleCompareClick}
                  onCancelCompare={handleCancelCompare}
                  onCompareRuns={handleCompareRuns}
                  onTestRunSelect={handleTestRunSelect}
                />
              )}

              {activeTab === "runs" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-gc-med text-foreground">Workflow Runs</h3>
                    <p className="text-sm text-muted-foreground">
                      Workflow runs in this test set ({totalItems} total)
                    </p>
                  </div>

                  {isItemsLoading && items.length === 0 ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex items-center gap-4 py-4">
                          <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                          <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                          <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                          <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                        </div>
                      ))}
                    </div>
                  ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                      <div className="space-y-3">
                        <h3 className="text-lg font-gc-med text-foreground">No workflow runs</h3>
                        <p className="text-muted-foreground max-w-md">
                          This test set has no workflow runs yet.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-border hover:bg-transparent">
                            <TableHead className="w-[18%] text-left">Date</TableHead>
                            <TableHead className="w-[22%] text-left">Session ID</TableHead>
                            <TableHead className="w-[10%] text-center">Status</TableHead>
                            <TableHead className="w-[10%] text-left">Duration</TableHead>
                            <TableHead className="w-[10%] text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map((item) => {
                            const run = item.workflow_run;
                            if (!run) return null;
                            return (
                              <TableRow
                                key={item.id}
                                className="cursor-pointer hover:bg-accent/20 transition-colors border-b border-border/50"
                                onClick={() => handleRunClick(item)}
                              >
                                <TableCell className="py-4 text-left">
                                  <div className="space-y-1">
                                    <div className="text-sm font-gc-reg text-foreground">
                                      {run.date_created ? moment(run.date_created).format("MMM D, YYYY") : "-"}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {run.date_created ? moment(run.date_created).format("h:mm A") : ""}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="py-4 text-left">
                                  <div className="flex items-center gap-2">
                                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                                      {run.session_id?.length > 12
                                        ? `${run.session_id.substring(0, 6)}...${run.session_id.substring(run.session_id.length - 6)}`
                                        : run.session_id}
                                    </code>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(run.session_id || "");
                                      }}
                                    >
                                      <CopyIcon className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell className="py-4 text-center">
                                  <div className={cn(
                                    "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                                    run.status === "COMPLETED" && "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
                                    run.status === "FAILED" && "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
                                    run.status === "TIMEOUT" && "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
                                    run.status === "IMPORTED" && "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
                                    run.status === "RUNNING" && "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
                                    (!["COMPLETED", "FAILED", "TIMEOUT", "IMPORTED", "RUNNING"].includes(run.status)) && "bg-muted text-foreground"
                                  )}>
                                    {run.status}
                                  </div>
                                </TableCell>
                                <TableCell className="py-4 text-left">
                                  <span className="text-sm font-gc-reg text-foreground">
                                    {formatWorkflowDuration(run.profile)}
                                  </span>
                                </TableCell>
                                <TableCell className="py-4 text-right">
                                  <div className="flex justify-end">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="gap-1"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRunClick(item);
                                      }}
                                    >
                                      <EyeIcon className="w-3 h-3" />
                                      Details
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      {totalPages > 1 && (
                        <SmartPagination
                          currentPage={itemsPage}
                          totalPages={totalPages}
                          onPageChange={setItemsPage}
                          itemName="runs"
                          totalItems={totalItems}
                          itemsPerPage={PAGE_SIZE}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Modal */}
      <Dialog open={isComparisonModalVisible} onOpenChange={(open) => !open && handleCloseComparisonModal()}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompareArrows className="w-5 h-5" />
              Test Run Comparison
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Comparing {selectedTestRunIds.length} workflow test runs by test set item
            </p>
          </DialogHeader>
          <WorkflowTestRunComparison
            testRunIds={selectedTestRunIds}
            apiKey={apiKey}
            key={selectedTestRunIds.join("-")}
          />
          <DialogFooter>
            <Button onClick={handleCloseComparisonModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow Run Detail Modal (reused from Application overview) */}
      <WorkflowRunDetailModal
        run={selectedRun}
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) setSelectedRun(null);
        }}
      />
    </div>
  );
};

export default WorkflowTestSetDetail;
